import { browserTracingIntegration } from '@sentry/browser';
import * as Sentry from '@sentry/react';
import { config } from '@/config';
import { getClientName } from '@/utils/getClientName';
import { filterSentryEvent } from './filterSentryEvent';
import { rateLimitSentryEvent } from './rateLimitSentryEvent';
import { redactSensitiveUrlsDeep } from './redactSensitiveData';
import { scrubSentryEvent } from './scrubSentryEvent';
import { shouldTraceRequest } from './shouldTraceRequest';

Sentry.init({
  enabled: config.sentry.enabled,
  environment: config.env,
  dsn: config.sentry.dsn,
  integrations: [
    browserTracingIntegration({
      // Skip spans for large user-uploaded asset downloads so they don't trip
      // Sentry's non-actionable "Large HTTP Payload" detector (WEB-CLIENT-1F4).
      shouldCreateSpanForRequest: shouldTraceRequest,
    }),
  ],
  tracesSampleRate: 0.2,
  normalizeDepth: 10,
  maxValueLength: 10_000,
  allowUrls: ['https://agendahero.com', 'https://server.dev'],
  release: config.release,
  // Drop the noise first, collapse whatever is left into one report per burst,
  // then redact request payloads out of what survives both.
  beforeSend: (event) => {
    const kept = filterSentryEvent(event);
    const unthrottled = kept && rateLimitSentryEvent(kept);
    return (
      unthrottled && redactSensitiveUrlsDeep(scrubSentryEvent(unthrottled))
    );
  },
  // Pageload transactions are named after the path, and spans and breadcrumbs
  // carry URLs, so the reset token would leave on traces that never reach
  // beforeSend.
  beforeSendTransaction: (event) => redactSensitiveUrlsDeep(event),
  beforeBreadcrumb: (breadcrumb) => redactSensitiveUrlsDeep(breadcrumb),
  ignoreErrors: [
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    // Safari aborts in-flight fetches when window.location.reload() runs after vite:preloadError (see src/index.tsx)
    /^cancelled$/i,
    "Can't find variable: BroadcastChannel",
    'Websocket closed: 4004, Bad response',
    'Promise.withResolvers is not a function',
    "Failed to read the 'localStorage' property from 'Window'",
    "Failed to read the 'sessionStorage' property from 'Window'",
    'Network request failed',
    // Generic network-layer fetch failures, usually with no stacktrace: the
    // browser's wording for a request that never completed (offline, flaky
    // connection, backgrounded tab, navigation-aborted request). Safari says
    // "Load failed", Chromium "Failed to fetch", Firefox "NetworkError"/"network
    // error". These are user-connectivity conditions, not defects in our code —
    // the same class we already drop as "Network request failed" (Sentry
    // WEB-CLIENT-4P, WEB-CLIENT-104, WEB-CLIENT-1H6, WEB-CLIENT-17T).
    'Load failed',
    'Failed to fetch',
    /network error/i,
    // Storage access blocked by the browser — private mode, disabled cookies,
    // or a sandboxed/cross-origin context — makes localStorage/cookie reads
    // throw a SecurityError (DOMException code 18, "The operation is
    // insecure."). In production this fires from our bundled analytics SDK
    // (@amplitude/analytics-core) reaching for localStorage on locked-down iOS
    // Safari — an environment restriction we can't act on (WEB-CLIENT-26D).
    'The operation is insecure',
    "Failed to execute 'transaction' on 'IDBDatabase'", // IndexDB not supported
    // Safari (esp. private mode / low disk) throws a DOMException from IndexedDB
    // when it can't persist an attachment Blob. It's a browser storage
    // restriction with no stacktrace, not our code (Sentry WEB-CLIENT-1BR).
    'Error preparing Blob/File data to be stored in object store',
    // Corporate/network content filters and WAFs (schools, districts) block the
    // domain and return their own HTML "Request Blocked" page, which our GraphQL
    // client surfaces as a 307 with a non-JSON body ("timeout waiting for policy
    // response"). This is the user's network policy, not our code — a 307 is
    // never a legitimate response from our API (Sentry WEB-CLIENT-26H).
    /GraphQL Error \(Code: 307\)/,
    // Injected native-app bridge (sendDataToNative / navigationPerformanceLogger)
    // that assumes an iOS WKWebView. It is absent in normal and in-app browsers
    // (e.g. the Facebook browser) and is not part of our codebase.
    /window\.webkit\.messageHandlers/,
    // WebExtension content scripts (e.g. the DuckDuckGo browser) call
    // chrome.runtime.sendMessage; when their background page/tab is gone it
    // rejects with "Invalid call to runtime.sendMessage(). Tab not found." This
    // arrives as an unhandled rejection with no stacktrace, so it can't be
    // matched by frame. Our code never calls runtime.sendMessage (WEB-CLIENT-1ET).
    /runtime\.sendMessage\(\).*Tab not found/i,
    // ProseMirror keeps the DOM selection in sync with its document by calling
    // Selection.collapse(node, offset). On mobile in-app WebViews (e.g. the
    // Facebook browser) a `selectionchange` can fire against a node the DOM has
    // already mutated, so the offset momentarily exceeds the node length and the
    // browser throws "IndexSizeError: Failed to execute 'collapse' on
    // 'Selection'". It's a transient browser/DOM selection-sync race entirely
    // within prosemirror-view — our code never calls Selection.collapse — and the
    // editor recovers on the next selection (Sentry WEB-CLIENT-1KA).
    /Failed to execute 'collapse' on 'Selection'/,
    // Android in-app WebViews (e.g. the Facebook browser) expose a native
    // bridge object via @JavascriptInterface. When the page unloads the native
    // side is torn down first, so a pending postMessage into that bridge throws
    // "Error invoking postMessage: Java object is gone" from injected, anonymous
    // frames that are not part of our bundle (Sentry WEB-CLIENT-26J).
    /Java object is gone/,
    // Some ancient/embedded browsers (seen on the Nintendo Switch's built-in
    // browser) ship no `Intl` global. luxon reaches for `new Intl.DateTimeFormat`
    // to resolve the default locale as soon as a date module evaluates, so the
    // chunk throws "ReferenceError: Can't find variable: Intl" (WebKit) /
    // "Intl is not defined" (Chromium) before the app can render. It's an
    // unsupported browser, not our code — polyfilling all of Intl isn't viable
    // (Sentry WEB-CLIENT-1MD).
    /Can't find variable: Intl|Intl is not defined/,
    // Snapchat's in-app browser injects a `SCDynimacBridge` global into the page
    // and references it from its own injected global-scope script. When that
    // script runs before (or without) the bridge being defined it throws
    // "ReferenceError: Can't find variable: SCDynimacBridge" (WebKit) /
    // "SCDynimacBridge is not defined" (Chromium) from code that is not part of
    // our bundle. The identifier exists nowhere in our source (Sentry
    // WEB-CLIENT-27D).
    /SCDynimacBridge/,
    // iOS WKWebView tears down its JS→native message bridge when the web view is
    // deallocated (e.g. the user navigates away / backgrounds an in-app browser)
    // while a `postMessage` to a native handler is still in flight. WebKit then
    // rejects it with "The WKWebView was deallocated before the message was
    // delivered" — a native-side lifecycle race with no stacktrace, surfaced
    // through the global unhandledrejection handler. Our code never posts to a
    // WKWebView bridge; this is the same class as the other native-bridge noise
    // above (Sentry WEB-CLIENT-27E).
    /WKWebView was deallocated/,
    // The backend's queue fails a magic job whose worker stopped renewing its
    // lock and stores the queue's own wording on the job ("job stalled more than
    // allowable limit"). Our poller rethrows that message so the user sees the
    // failure (see usePollMagicJobStatus), which files it in this project — but
    // it describes a worker that died or blocked its event loop, a server-side
    // infrastructure fault the server already records, with nothing for the
    // client to fix. The phrase exists nowhere in our source (Sentry
    // WEB-CLIENT-27C).
    /job stalled more than allowable limit/,
    // A rejection whose reason isn't an Error instance surfaces with no
    // stacktrace and Sentry's own generic wording, so it can never be traced to
    // a frame in our bundle. Seen only from injected third-party scripts
    // rejecting with a plain value/undefined, never from our own code, which
    // always rejects with an Error (Sentry WEB-CLIENT-1JS).
    'Non-Error promise rejection captured with value: undefined',
  ],
  initialScope: (scope) => {
    scope.setTags({
      version: config.version,
      client: getClientName(),
    });
    return scope;
  },
});

export { Sentry };
